import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Announcement bar.
 *
 * Fits every item side by side: they stay static and centered.
 * Does not fit: the bar divides its full width into equal columns, one per item
 * that fits, and advances one column every `interval` seconds. Equal columns keep
 * a partially visible item off screen entirely, and the leading item moves to the
 * end after each step, so the sequence loops without a jump.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @extends {Component<Refs>}
 */
class AnnouncementBar extends Component {
  requiredRefs = ['viewport', 'track'];

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  #mobileTimer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #frame = null;
  #paused = false;
  #columnWidth = 0;
  /** Below this width the CSS pins two items and JS rotation must stay off. */
  #mobileQuery = window.matchMedia('(max-width: 749px)');
  /** @type {Element[]} */
  #originalOrder = [];
  /** @type {(() => void) | null} */
  #transitionHandler = null;

  connectedCallback() {
    super.connectedCallback();

    // Observe the host, never the viewport: this component writes layout values
    // itself, and observing those would feed its own writes back as resizes.
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(this);

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#mobileQuery.addEventListener('change', this.#schedule);

    this.#originalOrder = [...this.refs.track.children];
    this.#layout();

    // Web fonts land after the first measurement and change every item width.
    document.fonts?.ready.then(this.#schedule);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#stopMobile();
    this.#clearTransition();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mobileQuery.removeEventListener('change', this.#schedule);
  }

  /** Re-measures after a section re-render in the theme editor. */
  updatedCallback() {
    this.#schedule();
  }

  #schedule = () => {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#layout();
    });
  };

  #layout() {
    const { track } = this.refs;

    // Below 749px items fade one at a time via [data-mobile-active] and ignore
    // DOM order: stop the desktop scroll timer, drop any pending transition,
    // restore the original block order and hand off to the mobile rotation.
    if (this.#mobileQuery.matches) {
      this.#stop();
      this.#clearTransition();
      this.#restoreOrder();
      this.removeAttribute('data-scrolling');
      this.removeAttribute('data-animating');
      track.style.transform = '';
      this.style.removeProperty('--announcement-bar-column');
      this.#layoutMobile();
      return;
    }

    this.#stopMobile();

    const items = /** @type {HTMLElement[]} */ ([...track.children].filter((el) => el instanceof HTMLElement));
    if (items.length === 0) return;

    // Measure the natural widths first: reset to the static layout so no column
    // width from a previous pass influences the result.
    this.#stop();
    this.removeAttribute('data-scrolling');
    track.style.transform = '';
    this.style.removeProperty('--announcement-bar-column');

    const available = this.#availableWidth();
    if (available <= 0) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const widths = items.map((item) => item.getBoundingClientRect().width);
    const total = widths.reduce((sum, width) => sum + width, 0) + gap * (items.length - 1);

    if (total <= Math.ceil(available)) return;

    // Size every column to the widest item, so each one fits in whichever column
    // it lands in while cycling.
    const widest = Math.max(...widths);
    const columns = Math.max(1, Math.min(items.length - 1, Math.floor((available + gap) / (widest + gap))));

    this.#columnWidth = (available - gap * (columns - 1)) / columns;
    this.style.setProperty('--announcement-bar-column', `${this.#columnWidth}px`);
    this.setAttribute('data-scrolling', '');

    if (prefersReducedMotion()) return;
    this.#start();
  }

  /** Content width of the bar, so its padding does not count as room for items. */
  #availableWidth() {
    const styles = getComputedStyle(this);
    const padding = parseFloat(styles.paddingInlineStart) + parseFloat(styles.paddingInlineEnd);
    return this.getBoundingClientRect().width - padding;
  }

  #start() {
    this.#timer = setInterval(() => this.#advance(), this.#intervalMs);
  }

  #stop() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Shared by the desktop scroll timer and the mobile fade timer. */
  get #intervalMs() {
    const seconds = Number(this.getAttribute('interval')) || 4;
    return seconds * 1000;
  }

  /**
   * Mobile: one message visible at a time via [data-mobile-active], fading
   * between items on a timer. Reduced motion keeps the first message shown
   * and never starts the timer, so the others stay out of view.
   */
  #layoutMobile() {
    const items = /** @type {HTMLElement[]} */ ([...this.refs.track.children].filter(
      (el) => el instanceof HTMLElement
    ));
    if (items.length === 0) return;

    if (!items.some((item) => item.hasAttribute('data-mobile-active'))) {
      for (const [index, item] of items.entries()) {
        item.toggleAttribute('data-mobile-active', index === 0);
      }
    }

    this.#stopMobile();
    if (items.length <= 1 || prefersReducedMotion()) return;
    this.#mobileTimer = setInterval(() => this.#advanceMobile(), this.#intervalMs);
  }

  #advanceMobile() {
    if (this.#paused) return;

    const items = /** @type {HTMLElement[]} */ ([...this.refs.track.children].filter(
      (el) => el instanceof HTMLElement
    ));
    if (items.length === 0) return;

    const currentIndex = items.findIndex((item) => item.hasAttribute('data-mobile-active'));
    const nextIndex = (currentIndex + 1) % items.length;
    if (currentIndex >= 0) items[currentIndex]?.removeAttribute('data-mobile-active');
    items[nextIndex]?.setAttribute('data-mobile-active', '');
  }

  #stopMobile() {
    if (this.#mobileTimer) clearInterval(this.#mobileTimer);
    this.#mobileTimer = null;
  }

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };

  /** Shifts the track one column, then recycles the leading item to the end. */
  #advance() {
    if (this.#paused) return;

    const { track } = this.refs;
    const first = track.firstElementChild;
    if (!(first instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;

    this.setAttribute('data-animating', '');
    track.style.transform = `translateX(-${this.#columnWidth + gap}px)`;

    this.#transitionHandler = () => {
      this.#transitionHandler = null;
      this.removeAttribute('data-animating');
      track.style.transform = '';
      track.appendChild(first);
    };
    track.addEventListener('transitionend', this.#transitionHandler, { once: true });
  }

  /** Drops a pending transition callback without letting it run. */
  #clearTransition() {
    if (!this.#transitionHandler) return;
    this.refs.track.removeEventListener('transitionend', this.#transitionHandler);
    this.#transitionHandler = null;
  }

  /** Undoes any rotation so the DOM order matches the source blocks again. */
  #restoreOrder() {
    const { track } = this.refs;
    for (const item of this.#originalOrder) track.appendChild(item);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
